/**
 * Plain-language helpers for the advanced graph editor (ADR-0058, "advanced is easy too").
 *
 * - Conditions: a small visual model (value · operator · value) that reads and writes the SAME
 *   spec expressions (`agent_platform_sdk.spec.expr`); anything it cannot represent stays raw.
 * - Values from earlier steps: the state keys upstream nodes write, with where they come from.
 * - Tool arguments: form fields from an action's input JSON Schema.
 * - Node ids from labels, and validation issues in plain words with a node to point at and,
 *   where safe, a one-click fix.
 */
import type { Classification, JsonSchemaLite, RegistryTool, RiskTier } from "../types";
import { specEdges } from "./graph";
import type { AgentSpec, SpecNode } from "./types";
import type { SpecIssue } from "./validate";
import { schemaKind, schemaOf } from "./access";
import { clearSetting, type UnusedSetting } from "./references";
import { withTeam, type Topology } from "./team";

// ---------------------------------------------------------------- conditions

export type ConditionOp = "filled" | "empty" | "equals" | "not_is" | "not_equals" | "more_than" | "at_least" | "less_than" | "at_most" | "approved" | "rejected" | "within" | "not_within";
export const CONDITION_OPS: readonly ConditionOp[] = ["filled", "empty", "equals", "not_is", "not_equals", "more_than", "at_least", "less_than", "at_most", "within", "not_within", "approved", "rejected"];
/** "Within a tolerance" (ADR-0103): compared with another value or a number, give or take an amount, a percentage or days. */
export const TOLERANCE_OPS: ReadonlySet<ConditionOp> = new Set(["within", "not_within"]);
/** Operators that take a value. */
export const OPS_WITH_VALUE: ReadonlySet<ConditionOp> = new Set(["equals", "not_is", "not_equals", "more_than", "at_least", "less_than", "at_most"]);
const NUMERIC: ReadonlySet<ConditionOp> = new Set(["more_than", "at_least", "less_than", "at_most"]);
const SYMBOL: Partial<Record<ConditionOp, string>> = { equals: "==", not_equals: "!=", more_than: ">", at_least: ">=", less_than: "<", at_most: "<=" };
const OP_OF: Record<string, ConditionOp> = { "==": "equals", "!=": "not_equals", ">": "more_than", ">=": "at_least", "<": "less_than", "<=": "at_most" };

export interface Clause {
  /** State path without the `state.` prefix, e.g. `invoice` or `critique.score`. */
  key: string;
  op: ConditionOp;
  /** As the builder typed it; quoted or not when formatted (see `literal`). */
  value?: string;
  /** "Within": the value compared with, as a state path (else `value` is a number). */
  other?: string;
  /** "Within": the tolerance as typed: 0.5, 2% or 3 days. */
  tolerance?: string;
}

const PATH = "[A-Za-z_][A-Za-z0-9_]*(?:\\.[A-Za-z_][A-Za-z0-9_]*)*";
const RE_EXISTS = new RegExp(`^exists\\(state\\.(${PATH})\\)$`);
const RE_MISSING = new RegExp(`^not exists\\(state\\.(${PATH})\\)$`);
const RE_COMPARE = new RegExp(`^state\\.(${PATH})\\s*(==|!=|>=|<=|>|<)\\s*(.+)$`);
/** `not state.x == v`: "is not" that also holds when the value is missing (ADR-0071). */
const RE_NOT_EQUAL = new RegExp(`^not\\s+\\(?\\s*state\\.(${PATH})\\s*==\\s*(.+?)\\s*\\)?$`);
const NUMBER = /^-?\d+(\.\d+)?$/;
const RE_WITHIN = new RegExp(`^(not\\s+)?within\\(state\\.(${PATH}),\\s*(?:state\\.(${PATH})|(-?\\d+(?:\\.\\d+)?)),\\s*("[^"]*"|'[^']*'|-?\\d+(?:\\.\\d+)?)\\)$`);

function unquote(v: string): string | null {
  const t = v.trim();
  if (NUMBER.test(t) || t === "true" || t === "false" || t === "null") return t;
  const m = /^"((?:[^"\\]|\\.)*)"$/.exec(t) ?? /^'((?:[^'\\]|\\.)*)'$/.exec(t);
  return m ? (m[1] as string).replace(/\\(.)/g, "$1") : null;
}

/** A value as an expression literal: numbers and booleans bare, everything else quoted. */
export function literal(v: string, numeric = false): string {
  const t = v.trim();
  if (numeric || NUMBER.test(t) || t === "true" || t === "false" || t === "null") return t;
  return JSON.stringify(v);
}

/** Reads `a and b and …` clauses; null when the expression needs the raw editor. */
export function parseCondition(expr: string | null | undefined): Clause[] | null {
  const src = (expr ?? "").trim();
  if (!src) return [];
  if (/\bor\b|\bin\b|\(\s*state|len\(/.test(src.replace(/(?:not )?exists\(state\.[\w.]+\)/g, "").replace(/(?:not\s+)?within\(state\.[^()]*\)/g, ""))) return null;
  const parts = src.split(/\s+and\s+/);
  const out: Clause[] = [];
  for (const raw of parts) {
    const p = raw.trim();
    let m = RE_MISSING.exec(p);
    if (m) {
      out.push({ key: m[1] as string, op: "empty" });
      continue;
    }
    m = RE_EXISTS.exec(p);
    if (m) {
      out.push({ key: m[1] as string, op: "filled" });
      continue;
    }
    m = RE_WITHIN.exec(p);
    if (m) {
      const tol = unquote(m[5] as string);
      if (tol === null) return null;
      out.push({ key: m[2] as string, op: m[1] ? "not_within" : "within", ...(m[3] ? { other: m[3] } : { value: m[4] as string }), tolerance: tol });
      continue;
    }
    m = RE_NOT_EQUAL.exec(p);
    if (m) {
      const v = unquote(m[2] as string);
      if (v === null) return null;
      out.push({ key: m[1] as string, op: "not_is", value: v });
      continue;
    }
    m = RE_COMPARE.exec(p);
    if (!m) return null;
    const key = m[1] as string;
    const sym = m[2] as string;
    const value = unquote(m[3] as string);
    if (value === null) return null;
    if (key.endsWith("_decision") && value === "reject" && (sym === "==" || sym === "!=")) {
      out.push({ key, op: sym === "==" ? "rejected" : "approved" });
      continue;
    }
    const op = OP_OF[sym] as ConditionOp;
    if (NUMERIC.has(op) && !NUMBER.test(value)) return null;
    out.push({ key, op, value });
  }
  return out;
}

export function formatClause(c: Clause): string {
  switch (c.op) {
    case "filled":
      return `exists(state.${c.key})`;
    case "empty":
      return `not exists(state.${c.key})`;
    case "approved":
      return `state.${c.key} != "reject"`;
    case "rejected":
      return `state.${c.key} == "reject"`;
    case "not_is":
      return `not state.${c.key} == ${literal(c.value ?? "")}`;
    case "within":
    case "not_within": {
      const tol = (c.tolerance ?? "").trim();
      const call = `within(state.${c.key}, ${c.other ? `state.${c.other}` : literal(c.value ?? "", true)}, ${NUMBER.test(tol) ? tol : JSON.stringify(tol)})`;
      return c.op === "within" ? call : `not ${call}`;
    }
    default:
      return `state.${c.key} ${SYMBOL[c.op] as string} ${literal(c.value ?? "", NUMERIC.has(c.op))}`;
  }
}

export function formatCondition(clauses: readonly Clause[]): string {
  return clauses.map(formatClause).join(" and ");
}

/** Problems with a clause the builder is still editing (shown inline; nothing is written). */
export function clauseProblem(c: Clause): "key" | "value" | "number" | null {
  if (!new RegExp(`^${PATH}$`).test(c.key)) return "key";
  if (TOLERANCE_OPS.has(c.op)) {
    if (c.other ? !new RegExp(`^${PATH}$`).test(c.other) : !NUMBER.test((c.value ?? "").trim())) return "value";
    if (!/^\s*\d+(\.\d+)?\s*(%|d|day|days)?\s*$/i.test(c.tolerance ?? "")) return "number";
    return null;
  }
  if (OPS_WITH_VALUE.has(c.op) && !(c.value ?? "").trim()) return "value";
  if (NUMERIC.has(c.op) && !NUMBER.test((c.value ?? "").trim())) return "number";
  return null;
}

// ---------------------------------------------------------------- values from earlier steps

export interface StateValue {
  key: string;
  /** The node that writes it, or null for the user's request / runtime values. */
  node: string | null;
  kind: "request" | "answer" | "result" | "documents" | "reply" | "decision" | "subagent" | "toolRequest";
}

/** The state key a node writes, as the orchestrator's compiler does. */
export function outputOf(n: SpecNode): StateValue | null {
  const out = typeof n.output_key === "string" && n.output_key ? n.output_key : null;
  switch (n.type) {
    case "llm":
      return { key: out ?? "messages", node: n.id, kind: "answer" };
    case "tool":
      return { key: out ?? `${n.id}_result`, node: n.id, kind: "result" };
    case "retrieval":
      return { key: out ?? "context", node: n.id, kind: "documents" };
    case "human_input":
      return out ? { key: out, node: n.id, kind: "reply" } : null;
    case "human_approval":
      return { key: `${n.id}_decision`, node: n.id, kind: "decision" };
    case "subgraph":
      return { key: out ?? "subgraph_result", node: n.id, kind: "subagent" };
    default:
      return null;
  }
}

/** Values available at `nodeId` (written by it or any node that can run before it). */
export function stateValues(spec: AgentSpec, nodeId?: string): StateValue[] {
  const nodes = spec.spec.graph.nodes;
  let allowed: Set<string> | null = null;
  if (nodeId) {
    const pred = new Map<string, string[]>();
    for (const [a, b] of specEdges(spec)) pred.set(b, [...(pred.get(b) ?? []), a]);
    allowed = new Set([nodeId]);
    const stack = [nodeId];
    while (stack.length) {
      for (const p of pred.get(stack.pop() as string) ?? []) {
        if (!allowed.has(p)) {
          allowed.add(p);
          stack.push(p);
        }
      }
    }
  }
  const out: StateValue[] = [{ key: "question", node: null, kind: "request" }];
  const seen = new Set(["question"]);
  for (const n of nodes) {
    if (allowed && !allowed.has(n.id)) continue;
    const v = outputOf(n);
    if (v && !seen.has(v.key)) {
      seen.add(v.key);
      out.push(v);
    }
    if (n.type === "llm" && (n.tools ?? []).length && !seen.has("tool_call")) {
      seen.add("tool_call");
      out.push({ key: "tool_call", node: n.id, kind: "toolRequest" });
    }
  }
  return out;
}

export const template = (key: string) => `{{ state.${key} }}`;
/** The state key of a value that is exactly one `{{ state.x }}` template, else null. */
export function templateKey(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const m = new RegExp(`^\\{\\{\\s*state\\.(${PATH})\\s*\\}\\}$`).exec(v.trim());
  return m ? (m[1] as string) : null;
}

// ---------------------------------------------------------------- tool arguments from a schema

export interface ArgField {
  name: string;
  required: boolean;
  type: "string" | "number" | "integer" | "boolean" | "object" | "array" | "any";
  description?: string;
  enum?: unknown[];
  /** The field's schema (the non-null branch of a nullable one): nested fields and list items. */
  schema?: JsonSchemaLite;
}

export function argFields(schema: JsonSchemaLite | undefined): ArgField[] {
  const root = schemaOf(schema);
  const props = root?.properties ?? {};
  const required = new Set(root?.required ?? []);
  return Object.entries(props).map(([name, raw]) => {
    const s = schemaOf(raw) ?? raw;
    return {
      name,
      required: required.has(name),
      type: schemaKind(s) ?? "any",
      ...(s.description ? { description: s.description } : {}),
      ...(s.enum ? { enum: s.enum } : {}),
      schema: s,
    };
  });
}

/** Whether a field is a group of named fields the builder fills one by one (`body` of a payment). */
export const isGroupField = (f: Pick<ArgField, "type" | "schema">): boolean => f.type === "object" && !!f.schema?.properties && Object.keys(f.schema.properties).length > 0;

/** Whether a field is a list of simple values (addresses, names, numbers), typed as tags. */
export function isSimpleList(f: Pick<ArgField, "type" | "schema">): boolean {
  const item = schemaKind(schemaOf(f.schema?.items));
  return f.type === "array" && (item === undefined || item === "string" || item === "number" || item === "integer");
}

/** A typed list: JSON as is, else one item per comma, semicolon or line (`a@x.com, b@y.com`). */
export function listValue(text: string, itemType?: string): unknown[] {
  const t = text.trim();
  if (t.startsWith("[")) {
    try {
      const v = JSON.parse(t) as unknown;
      if (Array.isArray(v)) return v;
    } catch {
      /* not JSON: split below */
    }
  }
  const items = t.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
  return itemType === "number" || itemType === "integer" ? items.map((x) => (NUMBER.test(x) ? Number(x) : x)) : items;
}

/** Turns what the builder typed into the argument value for the field's type. */
export function argValue(field: ArgField, text: string): unknown {
  const t = text.trim();
  if (templateKey(t)) return t;
  if ((field.type === "number" || field.type === "integer") && NUMBER.test(t)) return Number(t);
  if (field.type === "boolean" && (t === "true" || t === "false")) return t === "true";
  // A list typed as one value is still a list (G-io-18): "a@x.com" → ["a@x.com"].
  if (field.type === "array" && t) return listValue(t, schemaKind(schemaOf(field.schema?.items)));
  if (field.type === "object" && t) {
    try {
      return JSON.parse(t) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

export function argText(v: unknown): string {
  if (v === undefined || v === null) return "";
  return typeof v === "string" ? v : JSON.stringify(v);
}

/** Required values not set yet, as dotted paths (`body.amount` for a field inside a group). */
export function argsMissing(args: Record<string, unknown> | undefined, schema: JsonSchemaLite | undefined, prefix = ""): string[] {
  const out: string[] = [];
  for (const f of argFields(schema)) {
    const v = args?.[f.name];
    const path = prefix ? `${prefix}.${f.name}` : f.name;
    const plainObject = !!v && typeof v === "object" && !Array.isArray(v) && !("$literal" in (v as object));
    if (isGroupField(f) && plainObject) out.push(...argsMissing(v as Record<string, unknown>, f.schema, path));
    else if (f.required && (v === undefined || v === "" || (Array.isArray(v) && v.length === 0))) out.push(path);
  }
  return out;
}

// ---------------------------------------------------------------- node ids from labels

const NODE_ID = /^[a-z][a-z0-9_]{0,40}$/;

/** A node id from a label: lowercase ASCII words joined by `_` (empty when nothing Latin). */
export function idFromLabel(label: string): string {
  const s = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)
    .replace(/_+$/, "");
  return NODE_ID.test(s) ? s : "";
}

/** Whether an id looks generated (the node type with an optional `_n`, or from the old label). */
export function isAutoId(node: SpecNode, previousLabel?: string): boolean {
  return new RegExp(`^${node.type}(_\\d+)?$`).test(node.id) || (!!previousLabel && node.id === idFromLabel(previousLabel));
}

// ---------------------------------------------------------------- validation in plain words

export type PlainFix =
  | { kind: "grant"; tool: string; action: string }
  | { kind: "knowledge"; collection: string }
  | { kind: "dropLlmTool"; node: string; tool: string }
  /** Raise the agent's data level (ADR-0087). */
  | { kind: "level"; level: Classification }
  /** Raise the agent's risk tier (ADR-0087). */
  | { kind: "tier"; tier: RiskTier }
  /** Remove a setting nothing uses when the agent runs (Builder sweep). */
  | { kind: "clear"; what: UnusedSetting }
  /** Build a team's steps again from the team (ADR-0093). */
  | { kind: "rebuildTeam" };

export interface PlainIssue {
  /** i18n key under `builder:plain.issue`. */
  code: string;
  params: Record<string, string>;
  /** Node to select ("Show"). */
  node?: string;
  /** Agent-tab field to point at. */
  field?: string;
  fix?: PlainFix;
  /** The validator's own words, kept for experts. */
  raw: string;
}

const FIELD_OF: Record<string, string> = {
  tool: "tool",
  action: "action",
  collection: "collection",
  prompt: "prompt",
  subject_key: "subjectKey",
  question: "question",
  output_key: "outputKey",
  routes: "routes",
  default: "default",
  branches: "branches",
  join: "join",
  agent: "agent",
  entrypoint: "entrypoint",
  args: "args",
  max_iterations: "maxIterations",
};

/** Fields each node type must have (the schema's oneOf reports every branch's; keep the node's). */
const REQUIRED: Record<string, string[]> = {
  llm: ["prompt"],
  tool: ["tool", "action"],
  router: ["routes", "default"],
  human_approval: ["subject_key"],
  human_input: ["question", "output_key"],
  parallel: ["branches", "join"],
  subgraph: ["agent"],
  retrieval: ["collection"],
  python: ["entrypoint"],
};

export function plainIssue(spec: AgentSpec, issue: SpecIssue, tools: readonly RegistryTool[] = [], lang: "ar" | "en" = "en"): PlainIssue {
  const nodes = spec.spec.graph.nodes;
  // Steps by their plain name, never their id (G-flow-13): Finish, or the id in words.
  const label = (id: string) => {
    const n = nodes.find((x) => x.id === id);
    const l = n?.label ? (lang === "ar" ? n.label.ar || n.label.en : n.label.en || n.label.ar) : "";
    if (l) return l;
    if (n?.type === "end") return lang === "ar" ? "النهاية" : "Finish";
    const words = id.replace(/_/g, " ").trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
  };
  const toolName = (ref: string) => {
    const t = tools.find((x) => x.ref === ref);
    return t ? (lang === "ar" ? t.name.ar || t.name.en : t.name.en || t.name.ar) : ref;
  };
  const raw = `${issue.path} ${issue.message}`;
  const msg = issue.message;
  let m: RegExpExecArray | null;
  if ((m = /^tool node (\S+) uses (\S*)\.(\S*) not granted/.exec(msg))) {
    const [, node, tool, action] = m as unknown as [string, string, string, string];
    if (!tool || !action) return { code: "nodeFieldMissing", params: { step: label(node), field: tool ? "action" : "tool" }, node, raw };
    return { code: "toolNotAllowed", params: { step: label(node), tool: toolName(tool), action }, node, fix: { kind: "grant", tool, action }, raw };
  }
  if ((m = /^llm node (\S+) offers tool (\S+) not granted/.exec(msg))) {
    const [, node, tool] = m as unknown as [string, string, string];
    return { code: "llmToolNotAllowed", params: { step: label(node), tool: toolName(tool) }, node, fix: { kind: "dropLlmTool", node, tool }, raw };
  }
  if ((m = /^retrieval node (\S+) uses undeclared collection (\S+)/.exec(msg))) {
    const [, node, collection] = m as unknown as [string, string, string];
    return { code: "collectionNotDeclared", params: { step: label(node), collection }, node, fix: { kind: "knowledge", collection }, raw };
  }
  if ((m = /^llm node (\S+) has both a saved prompt and instructions/.exec(msg))) return { code: "promptBoth", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^llm node (\S+) has neither a saved prompt nor instructions/.exec(msg))) return { code: "promptNone", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^parallel (\S+) mixes /.exec(msg))) return { code: "parallelMixed", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  // ADR-0102: a level of an approval chain that doesn't say who signs it.
  if ((m = /^approval rule (\S+)\.(\S+) level (\d+): (\w+)$/.exec(msg))) {
    const [, tool, action, level, code] = m as unknown as [string, string, string, string, string];
    return { code: `approvalLevel_${code}`, params: { tool: toolName(tool), action, level }, field: "approvals", raw };
  }
  if ((m = /^approval (\S+) level (\d+): (\w+)$/.exec(msg))) {
    const [, node, level, code] = m as unknown as [string, string, string, string];
    return { code: `approvalStepLevel_${code}`, params: { step: label(node), level }, node, raw };
  }
  if ((m = /^approval rule targets (\S+)\.(\S+) not granted/.exec(msg))) {
    return { code: "approvalNotAllowed", params: { tool: toolName(m[1] as string), action: m[2] as string }, field: "grants", fix: { kind: "grant", tool: m[1] as string, action: m[2] as string }, raw };
  }
  if ((m = /^unreachable nodes: (.+)$/.exec(msg))) {
    const ids = (m[1] as string).replace(/[[\]']/g, "").split(/,\s*/).filter(Boolean);
    return { code: "unreachable", params: { steps: ids.map(label).join(", ") }, ...(ids[0] ? { node: ids[0] } : {}), raw };
  }
  if ((m = /^edge (\S+)->(\S+) references unknown node (\S+)/.exec(msg))) {
    return { code: "edgeUnknown", params: { from: label(m[1] as string), to: m[3] as string }, node: m[1] as string, raw };
  }
  if ((m = /^step (\S+) on_limit targets unknown node '?([^'\s]+)'?/.exec(msg))) return { code: "limitUnknown", params: { step: label(m[1] as string), to: m[2] as string }, node: m[1] as string, raw };
  if ((m = /^step (\S+) on_limit cannot go back to the same step/.exec(msg))) return { code: "limitSelf", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^step (\S+) has on_limit but no max_iterations/.exec(msg))) return { code: "limitNeedsRepeat", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^tool node (\S+) on_error targets unknown node '?([^'\s]+)'?/.exec(msg))) return { code: "failUnknown", params: { step: label(m[1] as string), to: m[2] as string }, node: m[1] as string, raw };
  if ((m = /^tool node (\S+) on_error cannot go back to the same step/.exec(msg))) return { code: "failSelf", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^table (\S+) needs two tables/.exec(msg)) || /^matching needs two tables/.test(msg)) return { code: "tableMatch", params: { step: m ? label(m[1] as string) : "" }, ...(m ? { node: m[1] as string } : {}), raw };
  if ((m = /^table (\S+) needs rows/.exec(msg)) || /^grouping needs the rows/.test(msg)) return { code: "tableGroup", params: { step: m ? label(m[1] as string) : "" }, ...(m ? { node: m[1] as string } : {}), raw };
  if ((m = /^foreach (\S+) needs a list/.exec(msg))) return { code: "loopList", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^(?:a )?for-?each (\S+) needs (?:steps or an agent|either steps or an agent)/.exec(msg))) return { code: "loopBody", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^for-?each (\S+): the arrow (\S+)->(\S+) leaves or enters/.exec(msg))) return { code: "loopArrow", params: { step: label(m[1] as string), from: label(m[2] as string), to: label(m[3] as string) }, node: m[1] as string, raw };
  if ((m = /^calculate (\S+) works out (.+) more than once/.exec(msg))) return { code: "calcTwice", params: { step: label(m[1] as string), values: m[2] as string }, node: m[1] as string, raw };
  if ((m = /^calculate (\S+) value (\S+) has no formula/.exec(msg))) return { code: "calcEmpty", params: { step: label(m[1] as string), value: m[2] as string }, node: m[1] as string, raw };
  if ((m = /^calculate (\S+) value (\S+?):? (has an unfinished formula|invalid formula)/.exec(msg))) return { code: "calcFormula", params: { step: label(m[1] as string), value: m[2] as string }, node: m[1] as string, raw };
  if ((m = /^wait (\S+) waits for nothing/.exec(msg))) return { code: "waitNothing", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^wait (\S+) needs an event name/.exec(msg))) return { code: "waitEventName", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^wait (\S+) needs a value to match events on/.exec(msg))) return { code: "waitEventKey", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^wait (\S+) needs a date and time/.exec(msg))) return { code: "waitAt", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^wait (\S+) on_give_up cannot go back/.exec(msg))) return { code: "waitGiveUpSelf", params: { step: label(m[1] as string) }, node: m[1] as string, raw };
  if ((m = /^entry (\S+) is not a node/.exec(msg))) return { code: "entryMissing", params: { id: m[1] as string }, raw };
  if ((m = /^duplicate node ids: (.+)$/.exec(msg))) return { code: "duplicateIds", params: { ids: m[1] as string }, raw };
  if ((m = /^node id (\S+) must be/.exec(msg))) return { code: "badId", params: { id: m[1] as string }, node: m[1] as string, raw };
  if (issue.path === "/metadata/name") return { code: "agentName", params: {}, field: "name", raw };
  if (issue.path === "/metadata/version") return { code: "agentVersion", params: {}, field: "version", raw };
  if (/^autonomous agents/.test(msg)) return { code: "autonomous", params: {}, field: "risk", raw };
  // Schema issues on a node: /spec/graph/nodes/<i>[/<field>…]. The schema's oneOf reports every
  // node type's complaints; only those about this node's own fields are worth showing.
  if ((m = /^\/spec\/graph\/nodes\/(\d+)(?:\/([a-z_]+))?(?:\/(ar|en))?/.exec(issue.path))) {
    const n = nodes[Number(m[1])];
    if (n) {
      const own = REQUIRED[n.type] ?? [];
      if (m[2] === "label" && m[3]) return { code: "nodeLabel", params: { step: label(n.id), lang: m[3] }, node: n.id, raw };
      const reported = /must have required property '([a-z_]+)'/.exec(msg)?.[1];
      const missing = reported ? (own.includes(reported) ? reported : own.find((f) => n[f] === undefined || n[f] === "")) : undefined;
      if (missing) return { code: "nodeFieldMissing", params: { step: label(n.id), field: FIELD_OF[missing] ?? missing }, node: n.id, raw };
      if (m[2] && own.includes(m[2])) return { code: "nodeFieldInvalid", params: { step: label(n.id), field: FIELD_OF[m[2]] ?? m[2] }, node: n.id, raw };
      return { code: "nodeInvalid", params: { step: label(n.id) }, node: n.id, raw };
    }
  }
  if (issue.path.startsWith("/metadata/description") || issue.path.startsWith("/spec/role") || issue.path.startsWith("/spec/goals")) {
    return { code: "agentText", params: {}, field: "texts", raw };
  }
  return { code: "other", params: { detail: raw }, raw };
}

/**
 * Plain issues for a list of validator issues: one line per problem, and no generic "isn't
 * complete" line for a node that already has a specific one.
 */
export function plainIssues(spec: AgentSpec, issues: readonly SpecIssue[], tools: readonly RegistryTool[] = [], lang: "ar" | "en" = "en"): PlainIssue[] {
  const all = issues.map((i) => plainIssue(spec, i, tools, lang));
  const specific = new Set(all.filter((p) => p.node && p.code !== "nodeInvalid").map((p) => p.node));
  const seen = new Set<string>();
  return all.filter((p) => {
    if (p.code === "nodeInvalid" && specific.has(p.node)) return false;
    const k = `${p.code}|${p.node ?? ""}|${JSON.stringify(p.params)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Applies a plain fix to the spec (pure). */
export function applyFix(spec: AgentSpec, fix: PlainFix): AgentSpec {
  if (fix.kind === "grant") {
    const tools = spec.spec.tools ?? [];
    const g = tools.find((x) => x.ref === fix.tool);
    if (g?.actions.includes(fix.action)) return spec;
    const next = g ? tools.map((x) => (x.ref === fix.tool ? { ...x, actions: [...x.actions, fix.action] } : x)) : [...tools, { ref: fix.tool, actions: [fix.action] }];
    return { ...spec, spec: { ...spec.spec, tools: next } };
  }
  if (fix.kind === "knowledge") {
    const k = spec.spec.knowledge ?? [];
    return k.some((x) => x.collection === fix.collection) ? spec : { ...spec, spec: { ...spec.spec, knowledge: [...k, { collection: fix.collection, top_k: 8 }] } };
  }
  if (fix.kind === "level") return { ...spec, spec: { ...spec.spec, permissions: { ...spec.spec.permissions, max_classification: fix.level } } };
  if (fix.kind === "clear") return clearSetting(spec, fix.what);
  if (fix.kind === "rebuildTeam") return withTeam(spec, (spec.spec.topology as Topology | null | undefined) ?? null);
  if (fix.kind === "tier") return { ...spec, spec: { ...spec.spec, risk_tier: Math.max(spec.spec.risk_tier, fix.tier) as RiskTier } };
  const nodes = spec.spec.graph.nodes.map((n) => (n.id === fix.node ? { ...n, tools: (n.tools ?? []).filter((t) => t !== fix.tool) } : n));
  return { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes } } };
}
